import i18next from 'i18next';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ChevronLeftIcon, SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Badge,
	Button,
	Container,
	Empty,
	Heading3,
	Input,
	Jumbo,
	Paragraph,
	Pill,
	Skeleton,
	Switch,
	Table,
	tokens,
} from '@treams/treams-ui';

type FeatureOverview = graphql.FeatureWorkspaceOverviewQuery['featureWorkspaceOverview'][number];

const TEST_WORKSPACE_PATTERNS = ['demo', 'test', 'treams', 'onboarding', 'lizannedv', 'acceptance'];

function isTestWorkspace(workspaceID: string): boolean {
	const lower = workspaceID.toLowerCase();
	return TEST_WORKSPACE_PATTERNS.some((p) => lower.includes(p));
}

type WorkspaceStatus = {
	workspaceID: string;
	status: graphql.WorkspaceFeatureStatus;
	explicit: boolean;
};

const statusColors: Record<graphql.WorkspaceFeatureStatus, string> = {
	[graphql.WorkspaceFeatureStatus.Enabled]: 'green',
	[graphql.WorkspaceFeatureStatus.Superuser]: 'blue',
	[graphql.WorkspaceFeatureStatus.Disabled]: 'danger',
	[graphql.WorkspaceFeatureStatus.Restricted]: 'lightBlue',
};

const statusOrder: Record<graphql.WorkspaceFeatureStatus, number> = {
	[graphql.WorkspaceFeatureStatus.Enabled]: 0,
	[graphql.WorkspaceFeatureStatus.Superuser]: 1,
	[graphql.WorkspaceFeatureStatus.Disabled]: 2,
	[graphql.WorkspaceFeatureStatus.Restricted]: 3,
};

type SortField = 'status' | 'source';
type SortDirection = 'asc' | 'desc';
type SortState = { field: SortField; direction: SortDirection } | null;

function toggleSort(current: SortState, field: SortField): SortState {
	if (current?.field !== field) return { field, direction: 'asc' };
	if (current.direction === 'asc') return { field, direction: 'desc' };
	return null;
}

function sortWorkspaces(list: WorkspaceStatus[], sort: SortState): WorkspaceStatus[] {
	if (!sort) return list;
	const sorted = [...list];
	const dir = sort.direction === 'asc' ? 1 : -1;
	sorted.sort((a, b) => {
		if (sort.field === 'status') {
			return (statusOrder[a.status] - statusOrder[b.status]) * dir;
		}
		const aVal = a.explicit ? 0 : 1;
		const bVal = b.explicit ? 0 : 1;
		return (aVal - bVal) * dir;
	});
	return sorted;
}

function sortIndicator(sort: SortState, field: SortField): string {
	if (sort?.field !== field) return '';
	return sort.direction === 'asc' ? ' ▲' : ' ▼';
}

const SortableHeader = styled(Box)`
	cursor: pointer;
	user-select: none;
`;

function useFeatureLabel(featureName: string) {
	const { t } = useTranslation();
	const featureKey = `settings.organization.settings.${featureName.replace(/\./g, '_')}`;
	const label = i18next.exists(featureKey) ? t(featureKey) : featureName.replace(/\./g, '_');
	return label;
}

const stabilityTint: Record<graphql.FeatureStability, 'green' | 'primary' | 'lightBlue'> = {
	[graphql.FeatureStability.Alpha]: 'green',
	[graphql.FeatureStability.Beta]: 'primary',
	[graphql.FeatureStability.Stable]: 'lightBlue',
};

function defaultStatusForStability(
	stability: graphql.FeatureStability,
): graphql.WorkspaceFeatureStatus {
	if (stability === graphql.FeatureStability.Stable) {
		return graphql.WorkspaceFeatureStatus.Enabled;
	}
	return graphql.WorkspaceFeatureStatus.Disabled;
}

function buildWorkspaceStatuses(
	feature: FeatureOverview,
	allWorkspaceIDs: string[],
): WorkspaceStatus[] {
	const explicitMap = new Map(feature.workspaces.map((w) => [w.workspaceID, w.status]));
	const defaultStatus = defaultStatusForStability(feature.stability);

	return allWorkspaceIDs.map((id) => {
		const explicit = explicitMap.get(id);
		return {
			workspaceID: id,
			status: explicit ?? defaultStatus,
			explicit: explicit !== undefined,
		};
	});
}

function countStatuses(
	feature: FeatureOverview,
	workspaceIDs: string[],
): { enabled: number; superuser: number; disabled: number } {
	const includedExplicit = feature.workspaces.filter((w) => workspaceIDs.includes(w.workspaceID));

	const explicitEnabled = includedExplicit.filter(
		(w) => w.status === graphql.WorkspaceFeatureStatus.Enabled,
	).length;
	const explicitSuperuser = includedExplicit.filter(
		(w) => w.status === graphql.WorkspaceFeatureStatus.Superuser,
	).length;
	const explicitDisabled = includedExplicit.filter(
		(w) => w.status === graphql.WorkspaceFeatureStatus.Disabled,
	).length;

	const implicitCount = workspaceIDs.length - includedExplicit.length;

	if (feature.stability === graphql.FeatureStability.Stable) {
		return {
			enabled: explicitEnabled + implicitCount,
			superuser: explicitSuperuser,
			disabled: explicitDisabled,
		};
	}

	return {
		enabled: explicitEnabled,
		superuser: explicitSuperuser,
		disabled: explicitDisabled + implicitCount,
	};
}

function FeatureDetailView({
	feature,
	allWorkspaceIDs,
	includeTestWorkspaces,
	onToggleTestWorkspaces,
	onBack,
}: {
	feature: FeatureOverview;
	allWorkspaceIDs: string[];
	includeTestWorkspaces: boolean;
	onToggleTestWorkspaces: (on: boolean) => void;
	onBack: () => void;
}) {
	const { t } = useTranslation();
	const label = useFeatureLabel(feature.featureName);
	const [search, setSearch] = useState('');
	const [sort, setSort] = useState<SortState>(null);

	const visibleWorkspaceIDs = useMemo(
		() =>
			includeTestWorkspaces
				? allWorkspaceIDs
				: allWorkspaceIDs.filter((id) => !isTestWorkspace(id)),
		[allWorkspaceIDs, includeTestWorkspaces],
	);

	const allStatuses = useMemo(
		() => buildWorkspaceStatuses(feature, visibleWorkspaceIDs),
		[feature, visibleWorkspaceIDs],
	);

	const counts = useMemo(
		() => countStatuses(feature, visibleWorkspaceIDs),
		[feature, visibleWorkspaceIDs],
	);

	const filtered = useMemo(() => {
		const searched = allStatuses.filter((w) =>
			w.workspaceID.toLowerCase().includes(search.toLowerCase()),
		);
		return sortWorkspaces(searched, sort);
	}, [allStatuses, search, sort]);

	return (
		<>
			<Jumbo
				title={label}
				description={feature.featureName}
				header={
					<Button
						icon={<ChevronLeftIcon />}
						title={t('jumbo.back.title')}
						type="textIcon"
						onClick={onBack}
					/>
				}
			/>
			<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
				<Flex mb={3} alignItems="center">
					<Pill tint={stabilityTint[feature.stability]} small mr={2}>
						{feature.stability}
					</Pill>
					<Pill tint="green" small mr={1}>
						{counts.enabled} enabled
					</Pill>
					{counts.superuser > 0 && (
						<Pill tint="primary" small mr={1}>
							{counts.superuser} superuser
						</Pill>
					)}
					<Pill tint="danger" small>
						{counts.disabled} disabled
					</Pill>
				</Flex>
				<Flex mb={3} alignItems="center">
					<Box flexGrow={1} mr={3}>
						<Input
							icon={<SearchIcon />}
							small
							value={search}
							onChange={setSearch}
							autoFocus
							placeholder="Search workspaces..."
						/>
					</Box>
					<Switch
						label="Include demo workspaces"
						on={includeTestWorkspaces}
						onChange={onToggleTestWorkspaces}
					/>
				</Flex>
				<Table>
					<Table.Header>
						<Table.Cell basis={0.4}>Workspace</Table.Cell>
						<Table.Cell basis={0.3}>
							<SortableHeader onClick={() => setSort(toggleSort(sort, 'status'))}>
								Status{sortIndicator(sort, 'status')}
							</SortableHeader>
						</Table.Cell>
						<Table.Cell basis={0.3}>
							<SortableHeader onClick={() => setSort(toggleSort(sort, 'source'))}>
								Source{sortIndicator(sort, 'source')}
							</SortableHeader>
						</Table.Cell>
					</Table.Header>
					{filtered.length > 0 ? (
						filtered.map((ws) => (
							<Table.Row key={ws.workspaceID}>
								<Table.Cell basis={0.4}>{ws.workspaceID}</Table.Cell>
								<Table.Cell basis={0.3}>
									<Flex alignItems="center">
										<Badge color={statusColors[ws.status]} size={12} />
										<Box ml={2}>{ws.status}</Box>
									</Flex>
								</Table.Cell>
								<Table.Cell basis={0.3}>
									{ws.explicit ? (
										<Pill tint="primary" small>
											explicit
										</Pill>
									) : (
										<Pill tint="lightBlue" small>
											default ({feature.stability.toLowerCase()})
										</Pill>
									)}
								</Table.Cell>
							</Table.Row>
						))
					) : (
						<Skeleton.Empty
							contents={
								<Empty
									icon={<SearchIcon />}
									title="No workspaces"
									description="No workspaces match your search"
								/>
							}
						>
							<Skeleton.Repeat count={3}>
								<Table.Row>
									<Table.Cell.Skeleton basis={0.4} />
									<Table.Cell.Skeleton basis={0.3} />
									<Table.Cell.Skeleton basis={0.3} />
								</Table.Row>
							</Skeleton.Repeat>
						</Skeleton.Empty>
					)}
				</Table>
			</Container>
		</>
	);
}

function FeatureRow({
	feature,
	workspaceIDs,
	onSelect,
}: {
	feature: FeatureOverview;
	workspaceIDs: string[];
	onSelect: (feature: FeatureOverview) => void;
}) {
	const label = useFeatureLabel(feature.featureName);
	const counts = useMemo(() => countStatuses(feature, workspaceIDs), [feature, workspaceIDs]);

	return (
		<Table.Row onClick={() => onSelect(feature)}>
			<Table.Cell basis={0.35}>
				<Box>
					<Heading3>{label}</Heading3>
					<Paragraph small color="slateBlue">
						{feature.featureName}
					</Paragraph>
				</Box>
			</Table.Cell>
			<Table.Cell basis={0.15}>
				<Pill tint={stabilityTint[feature.stability]} small>
					{feature.stability}
				</Pill>
			</Table.Cell>
			<Table.Cell basis={0.15}>
				{counts.enabled > 0 && (
					<Pill tint="green" small>
						{counts.enabled}
					</Pill>
				)}
			</Table.Cell>
			<Table.Cell basis={0.15}>
				{counts.superuser > 0 && (
					<Pill tint="primary" small>
						{counts.superuser}
					</Pill>
				)}
			</Table.Cell>
			<Table.Cell basis={0.2}>
				{counts.disabled > 0 && (
					<Pill tint="danger" small>
						{counts.disabled}
					</Pill>
				)}
			</Table.Cell>
		</Table.Row>
	);
}

function FeatureListView({
	onSelect,
	allWorkspaceIDs,
	includeTestWorkspaces,
	onToggleTestWorkspaces,
}: {
	onSelect: (feature: FeatureOverview) => void;
	allWorkspaceIDs: string[];
	includeTestWorkspaces: boolean;
	onToggleTestWorkspaces: (on: boolean) => void;
}) {
	const [search, setSearch] = useState('');

	const { data } = graphql.useFeatureWorkspaceOverviewQuery();

	const visibleWorkspaceIDs = useMemo(
		() =>
			includeTestWorkspaces
				? allWorkspaceIDs
				: allWorkspaceIDs.filter((id) => !isTestWorkspace(id)),
		[allWorkspaceIDs, includeTestWorkspaces],
	);

	const filtered = useMemo(() => {
		if (!data) return [];
		return data.featureWorkspaceOverview.filter((f) =>
			f.featureName.toLowerCase().includes(search.toLowerCase()),
		);
	}, [data, search]);

	if (!data) {
		return (
			<Table>
				<Table.Header>
					<Table.Cell basis={0.35}>Feature</Table.Cell>
					<Table.Cell basis={0.15}>Stability</Table.Cell>
					<Table.Cell basis={0.15}>Enabled</Table.Cell>
					<Table.Cell basis={0.15}>Superuser</Table.Cell>
					<Table.Cell basis={0.2}>Disabled</Table.Cell>
				</Table.Header>
				<Skeleton.Repeat count={8}>
					<Table.Row>
						<Table.Cell.Skeleton basis={0.35} />
						<Table.Cell.Skeleton basis={0.15} />
						<Table.Cell.Skeleton basis={0.15} />
						<Table.Cell.Skeleton basis={0.15} />
						<Table.Cell.Skeleton basis={0.2} />
					</Table.Row>
				</Skeleton.Repeat>
			</Table>
		);
	}

	return (
		<>
			<Flex mb={3} alignItems="center">
				<Box flexGrow={1} mr={3}>
					<Input
						icon={<SearchIcon />}
						small
						value={search}
						onChange={setSearch}
						autoFocus
						placeholder="Search features..."
					/>
				</Box>
				<Switch
					label="Include demo workspaces"
					on={includeTestWorkspaces}
					onChange={onToggleTestWorkspaces}
				/>
			</Flex>
			<Table>
				<Table.Header>
					<Table.Cell basis={0.35}>Feature</Table.Cell>
					<Table.Cell basis={0.15}>Stability</Table.Cell>
					<Table.Cell basis={0.15}>Enabled</Table.Cell>
					<Table.Cell basis={0.15}>Superuser</Table.Cell>
					<Table.Cell basis={0.2}>Disabled</Table.Cell>
				</Table.Header>
				{filtered.length > 0 ? (
					filtered.map((feature) => (
						<FeatureRow
							key={feature.featureName}
							feature={feature}
							workspaceIDs={visibleWorkspaceIDs}
							onSelect={onSelect}
						/>
					))
				) : (
					<Skeleton.Empty
						contents={
							<Empty
								icon={<SearchIcon />}
								title="No features found"
								description="No features match your search"
							/>
						}
					>
						<Skeleton.Repeat count={5}>
							<Table.Row>
								<Table.Cell.Skeleton basis={0.35} />
								<Table.Cell.Skeleton basis={0.15} />
								<Table.Cell.Skeleton basis={0.15} />
								<Table.Cell.Skeleton basis={0.15} />
								<Table.Cell.Skeleton basis={0.2} />
							</Table.Row>
						</Skeleton.Repeat>
					</Skeleton.Empty>
				)}
			</Table>
		</>
	);
}

export default function ModuleSettings() {
	const [selectedFeature, setSelectedFeature] = useState<FeatureOverview | null>(null);
	const [includeTestWorkspaces, setIncludeTestWorkspaces] = useState(false);

	const workspacesQuery = graphql.useWorkspacesMinimalQuery({
		variables: { filters: { isArchived: false }, first: 1000 },
	});

	const allWorkspaceIDs = useMemo(() => {
		if (!workspacesQuery.data) return [];
		return workspacesQuery.data.workspacesV2.edges
			.map((e) => e.node.id)
			.filter((id) => id !== 'treams');
	}, [workspacesQuery.data]);

	if (selectedFeature) {
		return (
			<FeatureDetailView
				feature={selectedFeature}
				allWorkspaceIDs={allWorkspaceIDs}
				includeTestWorkspaces={includeTestWorkspaces}
				onToggleTestWorkspaces={setIncludeTestWorkspaces}
				onBack={() => setSelectedFeature(null)}
			/>
		);
	}

	return (
		<>
			<Jumbo
				title="Modules"
				description="Overview of feature statuses across all workspaces. Click a feature to see which workspaces have it enabled."
			/>
			<Container mt={`${tokens.gap.page}px`} pb={`${tokens.gap.page}px`}>
				<FeatureListView
					onSelect={setSelectedFeature}
					allWorkspaceIDs={allWorkspaceIDs}
					includeTestWorkspaces={includeTestWorkspaces}
					onToggleTestWorkspaces={setIncludeTestWorkspaces}
				/>
			</Container>
		</>
	);
}
